<template>
  <section>
    <div v-if="loading">
      <div class="pb-1 pl-1">
        <b-skeleton type="button" width="200px" height="34px"></b-skeleton>
      </div>
      <b-skeleton-table
        :rows="10"
        :columns="fields.length"
      />
    </div>
    <template v-if="!loading">
      <b-button
        variant="primary"
        class="mb-1 ml-1"
        :to="`/${$route.params.accountId}/catalogs/schemas/create`"
        size="sm"
      >
        <GjIcon
          class="mr-50"
          name="Plus"
        />
        {{ $tk('ClientPortal.CatalogSet.CreateCatalogSchema') }}
      </b-button>
      <b-card
        no-body
        class="client-table--catalogs"
      >
        <b-table
          :items="wrapper"
          :fields="fields"
          class="client-table__data catalog-schema-table"
          show-empty
          :empty-text="$tk('ClientPortal.Catalogs.NoCatalogSchemasHaveBeenCreatedYet')"
          :current-page="currentPage"
          :per-page="perPage"
          responsive
          :tbody-tr-class="newRecordRowStyle"
        >
          <template #empty="scope">
            <div class="text-center p-3 client-table__empty">
              <div class="empty__client-table">
                <GjIcon
                  size="64px"
                  name="Book"
                />
                <div class="mb-50">
                  {{ scope.emptyText }}
                </div>
                <b-link :to="`/${$route.params.accountId}/catalogs/schemas/create`">
                  <GjIcon
                    size="14"
                    name="Plus"
                  />
                  <span class="create-text"> {{ $tk('ClientPortal.CatalogSet.CreateCatalogSchema') }}</span>
                </b-link>
              </div>
            </div>
          </template>
          <template #cell(schemaName)="data">
            <div class="d-flex align-items-center justify-content-between">
              <b-link :to="`/${$route.params.accountId}/catalogs/schemas/create/${data.item.id}`">
                {{ data.item.schemaName }}
              </b-link>
              <b-dropdown
                variant="link"
                toggle-class="text-decoration-none"
                no-caret
                button-class="p-0"
                right
              >
                <template v-slot:button-content>
                  <GjIcon name="Moreoptions_fill" />
                </template>
                <b-dropdown-item :to="`/${$route.params.accountId}/catalogs/schemas/create/${data.item.id}`">
                  <GjIcon
                    class="mr-50"
                    name="Edit"
                  />
                  {{ $tk('ClientPortal.Form.Edit') }}
                </b-dropdown-item>
                <b-dropdown-item @click="showDeleteDialog(data.item)">
                  <GjIcon
                    class="mr-50"
                    name="Delete"
                  />
                  {{ $tk('ClientPortal.Form.Delete') }}
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </template>
        </b-table>
        <b-card-footer
          v-if="wrapper.length > 0"
          class="client-table__footer d-flex"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            size="sm"
            class="my-0 mr-1"
            hide-goto-end-buttons
          >
            <template #prev-text>
              <GjIcon
                class="mr-25"
                name="ArrowLeft"
              />
            </template>
            <template #next-text>
              <GjIcon
                class="ml-25"
                name="ArrowRight"
              />
            </template>
          </b-pagination>
          <span>
            {{ $tk('ClientPortal.Pagination.Viewing') }} {{ fromCount }} {{ $tk('ClientPortal.Pagination.To') }} {{ toCount }} {{ $tk('ClientPortal.Pagination.Of') }} {{ totalRows }} {{ $tk('ClientPortal.Pagination.Items') }}
          </span>
        </b-card-footer>
      </b-card>
    </template>
    <b-modal
      ref="deleteCatalogSchemaModal"
      title="Delete Catalog Schema"
      centered
    >
      {{ $tk('ClientPortal.Catalogs.AreYouSureYouWantToDeleteThisCatalogSchema') }}
      <template v-slot:modal-footer="{ cancel }">
        <b-button
          variant="outline-secondary"
          size="sm"
          @click="cancel()"
        >
          {{ $tk('ClientPortal.Form.Actions.Cancel') }}
        </b-button>
        <b-button
          variant="danger"
          :disabled="confirmDialog.loading"
          size="sm"
          @click="deleteCatalogSchema(confirmDialog.catalogSchema)"
        >
          {{ $tk('ClientPortal.Form.Actions.Delete') }}
        </b-button>
      </template>
    </b-modal>
  </section>
</template>

<script>
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import notification from '@/mixins/toastification'
import CatalogSchemas from '@/api/client/catalogSchemas.service'
import { computed, ref } from '@vue/composition-api'
import store from '@/store'

export default {
  components: {
    GjIcon,
  },
  mixins: [notification],
  data() {
    return {
      wrapper: [],
      confirmDialog: {
        loading: false,
        catalogSchema: {},
      },
      loading: false,
      currentPage: 1,
      perPage: 10,
    }
  },
  computed: {
    fromCount() {
      return (this.currentPage - 1) * this.perPage + 1
    },
    toCount() {
      const toCount = this.fromCount + this.perPage - 1
      return Math.min(toCount, this.totalRows)
    },
    totalRows() {
      return this.wrapper.length
    },
    fields() {
      return [
        {
          key: 'schemaName',
          label: this.$tk('ClientPortal.Catalogs.Name'),
        },
      ]
    },
  },
  watch: {
    '$route.params.accountId': 'fetchData',
  },
  async mounted() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const response = await CatalogSchemas.getCatalogSchemas(this.$route.params.accountId)
        this.wrapper = response
      } catch (error) {
        this.error(error.message)
      } finally {
        this.loading = false
      }
    },
    showDeleteDialog(catalogSchema) {
      this.$set(this.confirmDialog, 'catalogSchema', catalogSchema)
      this.$refs.deleteCatalogSchemaModal.show()
    },
    async deleteCatalogSchema(catalogSchema) {
      this.confirmDialog.loading = true
      try {
        const response = await CatalogSchemas.deleteCatalogSchema(catalogSchema, this.$route.params.accountId)
        if (response.saved) {
          this.wrapper = this.wrapper.filter(x => x.id !== catalogSchema.id)
          this.success(response.message)
        } else { this.error(response.message) }
      } catch (error) {
        this.error(error.message)
      } finally {
        this.confirmDialog.loading = false
        this.$refs.deleteCatalogSchemaModal.hide()
      }
    },
  },
  setup() {
    const isCreatedNew = computed(() => store.state.app.isCreatedNew)
    const alreadySet = ref(false)
    const firstRow = ref(null)
    const newRecordRowStyle = () => {
      if (!alreadySet.value) {
        firstRow.value = Array.from(document.querySelectorAll('.catalog-schema-table > table > tbody > tr:first-child')).pop()
        if (firstRow.value) {
          if (isCreatedNew.value) {
            firstRow.value.classList.add('table-row-animation')
          } else if (firstRow.value.classList.contains('table-row-animation')) {
            firstRow.value.classList.remove('table-row-animation')
          }
          alreadySet.value = true
        }
      }
      return ''
    }
    if (isCreatedNew.value) {
      setTimeout(() => {
        store.commit('app/TOGGLE_NEW_CREATE_STATE', false)
        firstRow.value.classList.remove('table-row-animation')
      }, 3000)
    }
    return {
      isCreatedNew,
      newRecordRowStyle,
    }
  },
}
</script>
<style lang="scss">
@import '@/assets/scss/views/added_row_animation.scss';
</style>
